/**
 * Besökarens cookieval. Nödvändiga cookies kräver inget samtycke och finns
 * därför inte här — bara de kategorier besökaren faktiskt väljer mellan.
 */
export type ConsentChoice = {
  analytics: boolean;
  marketing: boolean;
};

export const CONSENT_COOKIE_NAME = "cl_consent";
// 12 månader — samma livslängd som språkvalet, och tillräckligt kort för att
// besökaren ska få frågan igen om ungefär ett år.
export const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
// Höjs när kategorierna ändras, så att gamla val inte tolkas som samtycke
// till något besökaren aldrig tillfrågats om.
const CONSENT_VERSION = "1";

export const ACCEPT_ALL: ConsentChoice = { analytics: true, marketing: true };
export const NECESSARY_ONLY: ConsentChoice = { analytics: false, marketing: false };

/** `v1.a1.m0` — kompakt, utan tecken som måste kodas i en cookie. */
export function serializeConsent(choice: ConsentChoice): string {
  return `v${CONSENT_VERSION}.a${choice.analytics ? 1 : 0}.m${choice.marketing ? 1 : 0}`;
}

/** Okänt, trasigt eller gammalt format ger null: då frågar vi igen. */
export function parseConsent(value: string | null | undefined): ConsentChoice | null {
  const match = value?.trim().match(/^v(\d+)\.a([01])\.m([01])$/);
  if (!match || match[1] !== CONSENT_VERSION) return null;
  return { analytics: match[2] === "1", marketing: match[3] === "1" };
}

/** Råvärdet ur cookien, "" om den saknas. Parsas med parseConsent. */
export function readConsentCookieValue(): string {
  const match = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${CONSENT_COOKIE_NAME}=`));
  return match?.slice(CONSENT_COOKIE_NAME.length + 1) ?? "";
}

export function writeConsentCookie(choice: ConsentChoice) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE_NAME}=${serializeConsent(choice)}; Path=/; Max-Age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

/**
 * Tar bort Google Analytics egna cookies (`_ga`, `_ga_<id>`) när samtycket
 * dras tillbaka. GA sätter dem på registrerbar domän (`.campuslyan.se`), så
 * båda varianterna rensas.
 */
export function clearAnalyticsCookies() {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = ["", `; Domain=${host}`];
  if (parts.length >= 2) domains.push(`; Domain=.${parts.slice(-2).join(".")}`);

  for (const cookie of document.cookie.split("; ")) {
    const name = cookie.split("=")[0];
    if (name !== "_ga" && !name.startsWith("_ga_")) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; Path=/; Max-Age=0${domain}`;
    }
  }
}
